import {
  ADMIN_DEFAULT_SECTION,
  defaultAdminSubtab,
  isAdminDestination,
  isAdminSection,
  type AdminSection
} from "./adminSections";

/**
 * Old admin URL compatibility.
 *
 * The admin terminal moved from 4 top tabs (providers / partners / shipping /
 * general) to the 8 sections in adminNavRegistry.ts. Digest emails, bell
 * notifications, bookmarks and stored dashboard locations still carry the old
 * `?section=&subtab=` pairs (and a few carry the pre-May sidenav ids such as
 * `control_room`), so every one of them resolves here onto a current pair.
 *
 * App.tsx and AdminShell both call this at first render and on popstate, and
 * normalise the address bar with replaceState (no extra history entry). Other
 * query params (`providerId`, `partner`, `jobId`, `recovery`, ...) are kept.
 * Unit tests: adminLocation.test.ts.
 */
export interface AdminLocation {
  section: AdminSection;
  subtab: string;
}

/** Old 4-tab layout: General subtab -> its new home. */
const GENERAL_SUBTAB_MAP: Readonly<Record<string, AdminLocation>> = {
  overview: { section: "home", subtab: "overview" },
  orders: { section: "orders", subtab: "orders" },
  fulfillment: { section: "orders", subtab: "fulfillment" },
  feedback: { section: "orders", subtab: "feedback" },
  "guest-support": { section: "orders", subtab: "guest-support" },
  "doodles-support": { section: "orders", subtab: "doodles-support" },
  "doodles-accounts": { section: "orders", subtab: "doodles-accounts" },
  "doodles-recovery": { section: "orders", subtab: "doodles-recovery" },
  settlement: { section: "money", subtab: "settlement" },
  pricing: { section: "money", subtab: "pricing" },
  fees: { section: "money", subtab: "fees" },
  "platform-fees": { section: "money", subtab: "fees" },
  coupons: { section: "money", subtab: "coupons" },
  forex: { section: "money", subtab: "forex" },
  "tax-report": { section: "money", subtab: "tax-report" },
  finance: { section: "money", subtab: "finance" },
  "country-launch": { section: "countries", subtab: "country-launch" },
  "international-shipping": { section: "countries", subtab: "policy" },
  "app-catalog": { section: "apps", subtab: "app-catalog" },
  traffic: { section: "apps", subtab: "traffic" },
  "doodles-config": { section: "apps", subtab: "doodles-config" },
  "matching-config": { section: "apps", subtab: "matching-config" },
  "matching-weights": { section: "apps", subtab: "matching-weights" },
  "standard-colors": { section: "apps", subtab: "standard-colors" },
  "first-party-keys": { section: "apps", subtab: "first-party-keys" },
  health: { section: "system", subtab: "health" },
  "app-status": { section: "system", subtab: "app-status" },
  maintenance: { section: "system", subtab: "maintenance" },
  announcements: { section: "system", subtab: "announcements" },
  "legal-documents": { section: "system", subtab: "legal-documents" },
  enforcement: { section: "system", subtab: "enforcement" },
  "storage-cleanup": { section: "system", subtab: "storage-cleanup" },
  audit: { section: "system", subtab: "audit" },
  "makerchip-config": { section: "system", subtab: "data-retention" }
};

/** Old Providers subtabs that moved to another section. */
const PROVIDERS_MOVED_MAP: Readonly<Record<string, AdminLocation>> = {
  coverage: { section: "countries", subtab: "coverage" },
  payouts: { section: "money", subtab: "payouts" },
  "wise-payouts": { section: "money", subtab: "wise-payouts" },
  "payout-runs": { section: "money", subtab: "payout-runs" }
};

const SHIPPING_SUBTABS: ReadonlyArray<string> = ["coverage", "policy", "bubbles", "lanes"];

/** Pre-May 12-section sidenav ids (no subtab of their own). */
const PRE_MAY_SECTION_MAP: Readonly<Record<string, AdminLocation>> = {
  control_room: { section: "providers", subtab: "directory" },
  applications: { section: "partners", subtab: "applications" },
  partners: { section: "partners", subtab: "directory" },
  settlements: { section: "money", subtab: "settlement" },
  quality: { section: "providers", subtab: "quality" },
  health: { section: "system", subtab: "health" },
  announcements: { section: "system", subtab: "announcements" },
  maintenance: { section: "system", subtab: "maintenance" },
  enforcement: { section: "system", subtab: "enforcement" },
  activity: { section: "system", subtab: "audit" },
  // The provider network announcement moved from Overview to Awaiting activation.
  network_live: { section: "providers", subtab: "awaiting" },
  pricing_benchmarks: { section: "money", subtab: "pricing" }
};

/**
 * Old section ids that are not current sections. Stored dashboard locations
 * may hold them, so the restore allowlist includes them.
 */
export const ADMIN_LEGACY_SECTION_IDS: ReadonlyArray<string> = [
  "general",
  "shipping",
  ...Object.keys(PRE_MAY_SECTION_MAP).filter((id) => !isAdminSection(id))
];

/**
 * An old General subtab's new home, or null when the id was never a General
 * subtab. App status checks name their page by General subtab id.
 */
export function resolveGeneralSubtab(subtab: string): AdminLocation | null {
  return Object.prototype.hasOwnProperty.call(GENERAL_SUBTAB_MAP, subtab)
    ? GENERAL_SUBTAB_MAP[subtab]!
    : null;
}

/** Resolve any current or old (section, subtab) pair to a current page. */
export function resolveAdminLocation(
  section: string | null | undefined,
  subtab: string | null | undefined
): AdminLocation {
  const sub = subtab ?? "";
  if (section === "general") {
    return resolveGeneralSubtab(sub) ?? { section: "home", subtab: "overview" };
  }
  if (section === "shipping") {
    return { section: "countries", subtab: SHIPPING_SUBTABS.includes(sub) ? sub : "coverage" };
  }
  if (section === "providers" && Object.prototype.hasOwnProperty.call(PROVIDERS_MOVED_MAP, sub)) {
    return PROVIDERS_MOVED_MAP[sub]!;
  }
  if (isAdminSection(section)) {
    return { section, subtab: isAdminDestination(section, sub) ? sub : defaultAdminSubtab(section) };
  }
  if (section && Object.prototype.hasOwnProperty.call(PRE_MAY_SECTION_MAP, section)) {
    return PRE_MAY_SECTION_MAP[section]!;
  }
  return { section: ADMIN_DEFAULT_SECTION, subtab: defaultAdminSubtab(ADMIN_DEFAULT_SECTION) };
}

/**
 * Resolve the address bar's admin location (or the given fallback pair when
 * the URL has no section) and rewrite `section`/`subtab` in place with
 * replaceState when they differ. Every other query param and the hash are
 * kept. Returns the resolved location.
 */
export function normalizeAdminUrl(fallback?: {
  section?: string | null;
  subtab?: string | null;
}): AdminLocation {
  const params = new URLSearchParams(window.location.search);
  const rawSection = params.get("section") ?? fallback?.section ?? null;
  const rawSubtab = params.get("subtab") ?? (params.has("section") ? null : fallback?.subtab ?? null);
  const resolved = resolveAdminLocation(rawSection, rawSubtab);
  if (params.get("section") !== resolved.section || params.get("subtab") !== resolved.subtab) {
    params.set("section", resolved.section);
    params.set("subtab", resolved.subtab);
    const url = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
    window.history.replaceState(window.history.state, "", url);
  }
  return resolved;
}
